import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, FileText, X, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const FilePreviewModal = ({ open, onOpenChange, fileUrl, fileName }) => {
    const [loading, setLoading] = useState(true);

    if (!fileUrl) return null;

    const cleanUrl = fileUrl.split('?')[0];
    const isImage = /\.(jpg|jpeg|png|webp|gif|heic)$/i.test(cleanUrl) || fileUrl.includes('image');
    const isPDF = /\.pdf$/i.test(cleanUrl);

    const handleDownload = async () => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: "Download Started",
                description: "Your file is being saved to your device.",
            });
        } catch (error) {
            console.error("Download error:", error);
            // Fallback to opening in new tab if blob download fails
            window.open(fileUrl, '_blank');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl w-[95vw] rounded-3xl p-0 overflow-hidden border-none bg-background/95 backdrop-blur-xl">
                <DialogHeader className="p-4 border-b bg-background/50 flex flex-row items-center justify-between space-y-0">
                    <DialogTitle className="text-base font-bold truncate pr-8">
                        {fileName || "File Preview"}
                    </DialogTitle>
                </DialogHeader>

                <div className="relative min-h-[300px] max-h-[70vh] flex items-center justify-center bg-muted/20">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    )}

                    {isImage ? (
                        <img
                            src={fileUrl}
                            alt={fileName}
                            className="max-w-full max-h-full object-contain"
                            onLoad={() => setLoading(false)}
                            onError={() => setLoading(false)}
                        />
                    ) : isPDF ? (
                        <div className="w-full h-[60vh] flex flex-col items-center justify-center p-12 text-center">
                            <FileText className="w-20 h-20 text-primary/20 mb-4" />
                            <p className="text-muted-foreground mb-6">
                                PDF Preview is best viewed in full screen or by downloading.
                            </p>
                            <Button
                                variant="outline"
                                className="rounded-xl gap-2"
                                onClick={() => window.open(fileUrl, '_blank')}
                            >
                                <ExternalLink className="w-4 h-4" />
                                Open in New Tab
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center p-12 text-center">
                            <FileText className="w-20 h-20 text-primary/20 mb-4" />
                            <p className="text-muted-foreground">Preview not available for this file type.</p>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-background/50 border-t flex gap-3">
                    <Button
                        className="flex-1 rounded-xl gap-2 py-6 h-auto font-bold"
                        onClick={handleDownload}
                    >
                        <Download className="w-5 h-5" />
                        Download File
                    </Button>
                    <Button
                        variant="outline"
                        className="rounded-xl py-6 h-auto"
                        onClick={() => onOpenChange(false)}
                    >
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FilePreviewModal;
